import { createI18n } from 'vue-i18n';
import en from '@/lang/en';
import fr from '@/lang/fr';
import de from '@/lang/de';
import es from '@/lang/es';
import it from '@/lang/it';
import nl from '@/lang/nl';
import da from '@/lang/da';
import pl from '@/lang/pl';
import cs from '@/lang/cs';
import ro from '@/lang/ro';
import pt from '@/lang/pt';
import ar from '@/lang/ar';

const DEFAULT_LOCALE = 'fr';
// 11 langues livrées + l'arabe (test). Chacune est surchargeable PAR STOREFRONT
// via le BO (onglet Traductions) — cf. fetchAndMergeTranslations.
const SUPPORTED_LOCALES = ['fr', 'en', 'de', 'es', 'it', 'nl', 'da', 'pl', 'cs', 'ro', 'pt', 'ar'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Langues de TEST : sélectionnables explicitement via `?lang=xx` mais JAMAIS
 * choisies par la détection navigateur. Leur bundle est vide (repli sur le
 * français) et l'interface n'est pas encore adaptée au RTL — un visiteur
 * arabophone doit donc continuer à recevoir le français, comme avant.
 *
 * `?lang=ar` reste le moyen de retester les traductions de thème maintenant que
 * la barre de test a été retirée.
 */
const TEST_ONLY_LOCALES: readonly string[] = ['ar'];

export const messages = {
    fr,
    en,
    de,
    es,
    it,
    nl,
    da,
    pl,
    cs,
    ro,
    pt,
    ar
} as Record<SupportedLocale, Record<string, string>>;

export const availableLocales = [
    { label: 'Français', flag: '🇫🇷', value: 'fr' },
    { label: 'English', flag: '🇬🇧', value: 'en' },
    { label: 'Deutsch', flag: '🇩🇪', value: 'de' },
    { label: 'Español', flag: '🇪🇸', value: 'es' },
    { label: 'Italiano', flag: '🇮🇹', value: 'it' },
    { label: 'Nederlands', flag: '🇳🇱', value: 'nl' },
    { label: 'Dansk', flag: '🇩🇰', value: 'da' },
    { label: 'Polski', flag: '🇵🇱', value: 'pl' },
    { label: 'Čeština', flag: '🇨🇿', value: 'cs' },
    { label: 'Română', flag: '🇷🇴', value: 'ro' },
    { label: 'Português', flag: '🇵🇹', value: 'pt' },
    // Test uniquement (bundle vide, interface non RTL) — cf. TEST_ONLY_LOCALES.
    { label: 'العربية (test)', flag: '🇸🇦', value: 'ar' }
];

/**
 * Langue forcée par l'URL (`?lang=fr` ou `&lang=fr`, y compris dans le hash SPA).
 * Renvoie une locale supportée, sinon null (=> on retombe sur la détection
 * navigateur). Accepte les codes régionaux (fr-FR => fr).
 */
function localeFromUrl(): SupportedLocale | null {
    try {
        const hash = window.location.hash || '';
        const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
        for (const qs of [window.location.search || '', hashQuery]) {
            const raw = (new URLSearchParams(qs).get('lang') || '').toLowerCase().trim();
            const short = raw.split('-')[0];
            if ((SUPPORTED_LOCALES as readonly string[]).includes(short)) {
                return short as SupportedLocale;
            }
        }
    } catch {
        /* window indisponible / URL malformée => détection navigateur */
    }
    return null;
}

/** Détection navigateur : code court (fr-FR => fr) si supporté, sinon défaut. */
function detectFromBrowser(): SupportedLocale {
    const short = String(navigator.language || DEFAULT_LOCALE)
        .toLowerCase()
        .split('-')[0];
    const isSelectable = (SUPPORTED_LOCALES as readonly string[]).includes(short) && !TEST_ONLY_LOCALES.includes(short);
    return isSelectable ? (short as SupportedLocale) : DEFAULT_LOCALE;
}

/**
 * Locale initiale : le paramètre d'URL `lang` prime s'il correspond à une langue
 * supportée ; sinon (absent ou inconnu) repli sur la détection navigateur.
 */
export function getCurrentLocale(): SupportedLocale {
    return localeFromUrl() ?? detectFromBrowser();
}

const i18n = createI18n({
    legacy: false,
    locale: getCurrentLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    availableLocales: [...SUPPORTED_LOCALES],
    messages
});

/**
 * Fusionne des traductions surchargées PAR STOREFRONT (BO, onglet Traductions).
 * Le JSON attendu est `{ <locale>: { Clef: 'valeur', ... }, ... }` pour n'importe
 * laquelle des 11 langues supportées. Les clés absentes gardent la valeur par
 * défaut du bundle (les traductions livrées ici).
 */
export async function fetchAndMergeTranslations(url: string) {
    try {
        if (!url) {
            return;
        }
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const fetched = (await response.json()) as Partial<Record<SupportedLocale, Record<string, string>>>;
        for (const locale of SUPPORTED_LOCALES) {
            const override = fetched?.[locale];
            if (override && typeof override === 'object') {
                Object.assign(messages[locale], override);
                i18n.global.setLocaleMessage(locale, messages[locale]);
            }
        }
    } catch (error) {
        console.error('Error fetching translations:', error);
    }
}

export default i18n;
